import {
  Suspense,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useContext,
} from 'react';

import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  Box,
  Button,
  ColorsV2,
  Flex,
  Text,
  useBreakpoint,
  useThemeColors,
} from '@electricjs/arc';
import styled from 'styled-components';

import ADPConsentRequiredBannerWrapper from '@/components/ADP/ADPConsentRequiredBannerWrapper';
import SideNav from '@/components/Nav/SideNav/SideNavWrapper';
import TopNav from '@/components/Nav/TopNav/TopNavWrapper';
import PageSpinner from '@/components/PageSpinner';
import { useAppSelector } from '@/redux/store';
import { OrganizationUserRoleSlugs } from '@/types/users';
import { useRouteReloader } from '@/hooks/useRouteReloader';
import { useGlobalUI } from '@/components/GlobalUIProvider';
import {
  PageTitleProvider,
  usePageTitleContext,
} from '@/contexts/PageTitleContext';
import { useAIChatFeatureFlags } from '@/hooks/useAIChatFeatureFlags';
import { EmbeddedAIChatPanel } from '@/components/AIChat/EmbeddedAIChatPanel';
import { ThemeContext, Themes } from '@/components/ThemeProvider';
import { useBannerHeight } from '@/hooks/useBannerHeight';
import { BANNER_HEIGHT } from '@/components/Nav/sizes';
import { EmployeesMapGate } from './EmployeesMapGate';
import { ErrorPage } from './ErrorPage';
import { getSecondDomainName, isValidExternalRedirectUrl } from './utils';

const DefaultPageContent = ({
  hasActiveBanner,
  bannerHeight,
}: {
  hasActiveBanner: boolean;
  bannerHeight: string;
}) => {
  const [showSideNav, setShowSideNav] = useState(true);
  const { md, lg } = useBreakpoint();
  const location = useLocation();
  const scrollableWrapperRef = useRef<HTMLElement>(null);

  const { ithEmbeddedAiChat, canAccessGigawattForSetupAssistant } =
    useAIChatFeatureFlags();
  const { theme } = useContext(ThemeContext);
  const isDarkTheme =
    theme?.name === Themes.DARK || theme?.name === Themes.SYNTHWAVE;
  const isSynthwave = theme?.name === Themes.SYNTHWAVE;
  const synthwaveBloom = isSynthwave
    ? {
        backgroundImage: [
          'radial-gradient(ellipse at 90% -10%, rgba(255, 45, 138, 0.10) 0%, transparent 52%)',
          'radial-gradient(ellipse at -5% 112%, rgba(8, 82, 92, 0.32) 0%, transparent 42%)',
        ].join(', '),
      }
    : undefined;

  useLayoutEffect(() => {
    setShowSideNav(Boolean(md));
  }, [md]);

  // Temporary workaround for react-router's behavior of retaining scroll position
  // when navigating between routes. This ensures the main scrollable content
  // resets to the top whenever the route changes.
  useLayoutEffect(() => {
    if (scrollableWrapperRef.current) {
      scrollableWrapperRef.current.scrollTo({ top: 0 });
    }
  }, [location.pathname]);

  const toggleSideNav = () => setShowSideNav(!showSideNav);

  // Medium to large screens:
  //   - The side bar always exists
  //   - Split into a main row:
  //     - Left: SideNav
  //     - Right: TopBar + Content (in a column)
  if (md) {
    return (
      <Box
        display="flex"
        flexDirection="row"
        height={hasActiveBanner ? `calc(100vh - ${bannerHeight})` : '100vh'}
        backgroundColor={
          isDarkTheme ? ColorsV2.BACKGROUND : ColorsV2.BACKGROUND_PAGE
        }
        style={synthwaveBloom}
        width="100vw"
        overflow="hidden">
        {/* ......... left side (SideNav) ......... */}
        <SideNav />
        {/* ......... right side (TopBar + Content + AI Panel) ......... */}
        <Box
          flexGrow="1" // right side: will grow in width (to the right)
          display="flex"
          flexDirection="column"
          overflowY="hidden">
          <TopNav />
          {/* ......... inner row: page content + embedded AI panel ......... */}
          <Box
            flexGrow="1" // grows in height (to the bottom)
            display="flex"
            flexDirection="row"
            overflowY="hidden">
            <Box
              flexGrow="1" // content: grows to fill remaining horizontal space
              overflowY="auto" // can scroll the content
              ref={scrollableWrapperRef}>
              <Suspense fallback={<PageSpinner />}>
                <ADPConsentRequiredBannerWrapper />
                <PageTitleAnnouncer />
                <EmployeesMapGate errorFallback={<ErrorPage />}>
                  <Outlet />
                </EmployeesMapGate>
              </Suspense>
            </Box>
            {/* Embedded AI panel — lg+ only, and only when the embedded flag is on; otherwise the overlay drawer is used */}
            {/* TODO(TF-677): use hasAnyGigawattAccess once free-plan setup-assistant access is no longer temporary */}
            {canAccessGigawattForSetupAssistant && lg && ithEmbeddedAiChat && (
              <EmbeddedAIChatPanel />
            )}
          </Box>
        </Box>
      </Box>
    );
  }

  // Small screens:
  //   - The side bar is controlled by the TopBar menu button
  //   - Split into a main column: TopBar + "inner row"
  //   - The inner row:
  //     - Left: SideNav
  //     - Right: Content
  return (
    <Box
      display="flex"
      flexDirection="column"
      overflow="hidden"
      height={hasActiveBanner ? `calc(100vh - ${bannerHeight})` : '100vh'}
      width="100vw">
      <TopNav onMenuClick={toggleSideNav} />
      <Box
        flexGrow="1" // will grow in height (to the bottom)
        display="flex"
        flexDirection="row"
        backgroundColor={
          isDarkTheme ? ColorsV2.BACKGROUND : ColorsV2.BACKGROUND_PAGE
        }
        style={synthwaveBloom}
        overflow="hidden">
        {/* ......... left side (SideNav) ......... */}
        {showSideNav ? <SideNav mobileOnMenuClick={toggleSideNav} /> : null}
        {/* ......... right side (Content) ......... */}
        <Box
          flexGrow="1" // will grow in width (to the right)
          overflowY="auto" // can scroll the content
          ref={scrollableWrapperRef}>
          <Suspense fallback={<PageSpinner />}>
            <ADPConsentRequiredBannerWrapper />
            <PageTitleAnnouncer />
            <EmployeesMapGate errorFallback={<ErrorPage />}>
              <Outlet />
            </EmployeesMapGate>
          </Suspense>
        </Box>
      </Box>
    </Box>
  );
};

const SROnly = styled.h1`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

// Renders a visually-hidden h1 on tablet+ so screen readers still have a page
// heading even though the visible title has moved into the TopNav.
const PageTitleAnnouncer = () => {
  const { md } = useBreakpoint();
  const { title } = usePageTitleContext();
  if (!title || !md) return null;
  return <SROnly>{title}</SROnly>;
};

const UnderlinedText = styled(Text)`
  text-decoration: underline;
`;

export const DefaultPageWrapper = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const location = useLocation();
  const loggedUser = useAppSelector(state => state.loggedUser);
  const hasProductSupportRole =
    (
      loggedUser?.organizationUserRoles?.filter(
        role => role.slug === OrganizationUserRoleSlugs.PRODUCT_SUPPORT
      ) || []
    ).length > 0;
  const organizationName = loggedUser?.organizationName;
  const totalBannerHeight = useBannerHeight();
  const { setBannerHeight } = useGlobalUI();
  const [bannerColor, shadowColor] = useThemeColors([
    ColorsV2.WARNING_LIGHTEST,
    ColorsV2.GRAY,
  ]);

  const [redirectUrl, setRedirectUrl] = useState<string | null>(null);

  const { theme } = useContext(ThemeContext);
  const isDarkTheme =
    theme?.name === Themes.DARK || theme?.name === Themes.SYNTHWAVE;

  const redirectBannerTextColor = isDarkTheme
    ? ColorsV2.TEXT
    : ColorsV2.PRIMARY;

  const [redirectBannerColor, redirectShadowColor] = useThemeColors([
    ColorsV2.INFO_LIGHTEST,
    ColorsV2.GRAY,
  ]);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const externalRedirectUrl = urlParams.get('externalRedirectUrl');
    if (
      externalRedirectUrl &&
      isValidExternalRedirectUrl(externalRedirectUrl)
    ) {
      sessionStorage.setItem('externalRedirectUrl', externalRedirectUrl);
      setRedirectUrl(externalRedirectUrl);

      urlParams.delete('externalRedirectUrl');
      const newSearch = urlParams.toString();
      const newPath = newSearch
        ? `${location.pathname}?${newSearch}`
        : location.pathname;
      navigate(newPath, { replace: true });
    } else {
      const storedRedirectUrl = sessionStorage.getItem('externalRedirectUrl');
      if (storedRedirectUrl) {
        setRedirectUrl(storedRedirectUrl);
      }
    }
  }, [location.search, location.pathname, navigate]);

  const handleClick = () => {
    if (location.pathname !== '/switcher') {
      navigate('/switcher');
    }
  };

  const handleRedirectClick = () => {
    if (redirectUrl) {
      window.location.href = redirectUrl;
    }
  };

  // **** Route interceptor **** //
  // This hook is used to reload the page when the app version changes
  useRouteReloader();

  // Publish this page's banner height into shared GlobalUI state so GlobalDialogs (mounted
  // higher up, outside this component's local state) can offset the drawer below the banners.
  useEffect(() => {
    setBannerHeight(totalBannerHeight);
    return () => setBannerHeight('0rem');
  }, [totalBannerHeight, setBannerHeight]);

  return (
    <PageTitleProvider>
      {hasProductSupportRole && (
        <Flex
          justifyContent="space-between"
          alignItems="center"
          py="0.5rem"
          px="2rem"
          height={BANNER_HEIGHT}
          width="100%"
          backgroundColor={bannerColor}
          boxShadow={`0 0 8px ${shadowColor}`}
          position="relative"
          zIndex="4" // Puts the banner above the TopBar & SideNav
          overflow="hidden">
          <Text variant="label-small">
            {t('defaultPageWrapper.productSupportBanner.message', {
              organizationName,
            })}
          </Text>
          <Button
            id="button-switch-orgs-banner"
            variant="link"
            intent="neutral"
            onClick={handleClick}>
            {t('defaultPageWrapper.productSupportBanner.switchOrgsButton')}
          </Button>
        </Flex>
      )}
      {redirectUrl && (
        <Flex
          justifyContent="flex-start"
          alignItems="center"
          py="0.5rem"
          px="2rem"
          height={BANNER_HEIGHT}
          width="100%"
          backgroundColor={redirectBannerColor}
          boxShadow={`0 0 8px ${redirectShadowColor}`}
          position="relative"
          zIndex="4"
          overflow="hidden">
          <Button
            id="button-redirect-banner"
            variant="link"
            intent="neutral"
            onClick={handleRedirectClick}>
            <UnderlinedText
              variant="label-small"
              color={redirectBannerTextColor}>
              {t('defaultPageWrapper.redirectBanner.goTo', {
                partnerName: getSecondDomainName(redirectUrl),
              })}
            </UnderlinedText>
          </Button>
        </Flex>
      )}

      <DefaultPageContent
        hasActiveBanner={hasProductSupportRole || Boolean(redirectUrl)}
        bannerHeight={totalBannerHeight}
      />
    </PageTitleProvider>
  );
};
